import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { PageLayout } from "../components/PageLayout";
import { SubmitBugDialog } from "../components/SubmitBugDialog";
import { BugTicketStatusBadge } from "../components/BugTicketStatusBadge";
import { Button, Card, EmptyState, Spinner, Text, View } from "../components/base";
import { Icon } from "../components/base/Icon";
import { useAppDispatch, useAppSelector } from "../redux";
import { bugTicketActions } from "../redux/reducers/bugTicketSlice";
import { formatRelative } from "../utils/adminFormat";

export const BugReportsPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showBugDialog, setShowBugDialog] = useState(false);

  const { ids, entities, isLoading } = useAppSelector((state) => state.bugTicket);
  const tickets = ids.map((id) => entities[id]).filter(Boolean);

  useEffect(() => {
    dispatch(bugTicketActions.get({}));
  }, [dispatch]);

  const handleDialogClose = () => {
    setShowBugDialog(false);
    // Pick up the ticket created through the dialog.
    dispatch(bugTicketActions.get({}));
  };

  return (
    <PageLayout
      title="Bug Reports"
      description="Bugs you've reported, their status, and replies from our team"
      headerAction={
        <Button
          variant="brand"
          size="sm"
          onClick={() => setShowBugDialog(true)}
        >
          <Icon
            name="bug_report"
            size={16}
            className="mr-1.5"
          />
          Report a bug
        </Button>
      }
    >
      <View className="mt-6 flex flex-col gap-3">
        {isLoading && tickets.length === 0 ? (
          <View className="flex items-center justify-center py-16">
            <Spinner size={28} />
          </View>
        ) : tickets.length === 0 ? (
          <EmptyState
            icon="bug_report"
            title="No bug reports yet"
            description="When you report a bug, it will show up here so you can track its status and read replies from our team."
          />
        ) : (
          tickets.map((ticket) => (
            <Card
              key={ticket.id}
              className="p-4 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600"
              onClick={() => navigate(`/bug-reports/${ticket.id}`)}
            >
              <View className="flex flex-row items-center gap-3">
                <View className="min-w-0 flex-1">
                  <View className="flex flex-row items-center gap-2">
                    <Text className="truncate text-sm font-medium text-gray-900 dark:text-white">{ticket.title}</Text>
                    <BugTicketStatusBadge status={ticket.status} />
                  </View>
                  <Text className="mt-1 block truncate text-xs text-gray-500 dark:text-gray-400">{ticket.description}</Text>
                </View>
                <View className="flex flex-col items-end gap-1 shrink-0">
                  <Text className="text-xs text-gray-400 dark:text-gray-500">Updated {formatRelative(String(ticket.updatedAt))}</Text>
                  <Icon
                    name="arrow_forward"
                    size={14}
                    className="text-gray-300 dark:text-gray-600"
                  />
                </View>
              </View>
            </Card>
          ))
        )}
      </View>

      <SubmitBugDialog
        isOpen={showBugDialog}
        onClose={handleDialogClose}
      />
    </PageLayout>
  );
};
